// Document preview (app.md §8). The pane asks the API for a short-lived signed
// URL for the upload's file and renders it with PDF.js onto canvases — one
// canvas per page in a scrollable pane, scrolled to the invoice's first page
// when its page span is known.
//
// The URL endpoint checks the caller's own document access before issuing
// anything. Since 2026-08-10 (owner ruling R2) a company approver at any of the
// three tiers DOES get a URL — for a packet holding one of their own invoices;
// the `uploads` row policy is what bounds it, not this pane. One upload can hold
// invoices for several companies and the pipeline cannot split it, so that
// packet is visible whole, and the exposure is accepted and recorded
// (security.md → PDF policy, docs/decisions.md).
// The pane is only rendered where canPreviewPdf(session) allows it, but
// that is a UI hint — the API is the authority.
//
// Rendering stays PDF.js rather than an iframe or the browser's plugin viewer:
// it draws pixels into canvases we control, which is what makes the per-page
// virtualization, the zoom and the page-span scroll possible at all.
import { useEffect, useRef, useState } from 'react'
import { Body1, Button, Caption1, Spinner, makeStyles, tokens } from '@fluentui/react-components'
import { OpenRegular, DocumentRegular, ZoomInRegular, ZoomOutRegular } from '@fluentui/react-icons'
import type { Upload } from '../lib/types'
import type { PdfDoc, PdfView } from './pdfRender'
import { getUploadPdfUrl } from '../lib/data'
import { errorMessage } from '../api/errors'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '8px 4px',
  },
  pages: {
    flex: 1,
    minHeight: '320px',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px',
    boxSizing: 'border-box',
  },
  zoomGroup: { display: 'flex', alignItems: 'center', gap: '2px' },
  zoomLabel: { minWidth: '42px', textAlign: 'center', color: tokens.colorNeutralForeground3 },
  placeholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: tokens.colorNeutralForeground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: '320px',
    padding: '24px',
    textAlign: 'center',
  },
})

// Session cache of signed URLs (uploadId → url + when it expires), so paging
// back and forth through a queue doesn't ask for a new URL every time. Bounded
// FIFO. A URL is dropped a little before it actually expires, so a render that
// starts on a cached URL can still finish.
const URL_CACHE_MAX = 5
const EXPIRY_MARGIN_MS = 30_000

const urlCache = new Map<string, { url: string; expiresAt: number }>()

function cacheUrl(uploadId: string, url: string, expiresAt: string | undefined) {
  if (urlCache.size >= URL_CACHE_MAX) {
    const oldest = urlCache.keys().next().value
    if (oldest) urlCache.delete(oldest)
  }
  const at = expiresAt ? Date.parse(expiresAt) : NaN
  urlCache.set(uploadId, { url, expiresAt: Number.isNaN(at) ? Date.now() + 300_000 : at })
}

function cachedUrl(uploadId: string): string | undefined {
  const hit = urlCache.get(uploadId)
  if (!hit) return undefined
  if (hit.expiresAt - EXPIRY_MARGIN_MS <= Date.now()) {
    urlCache.delete(uploadId)
    return undefined
  }
  return hit.url
}

type FetchState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error'; message: string }

// Capture PDF.js's internal warnings (it logs and carries on when a decoder or a
// font can't be loaded) so a degraded render says why instead of just looking
// wrong.
//
// Reentrant-safe: overlapping calls (e.g. rapid zoom) each register a sink;
// the console is patched once (originals captured on the first call) and
// restored only once every active call has finished — so a nested call can't
// restore a wrapped console over a still-running outer one.
const activeWarningSinks: string[][] = []
let savedConsole: Pick<Console, 'log' | 'warn' | 'error'> | null = null

function collectPdfWarnings<T>(run: () => Promise<T>): Promise<{ result: T; warnings: string[] }> {
  const warnings: string[] = []
  activeWarningSinks.push(warnings)
  if (!savedConsole) {
    savedConsole = { log: console.log, warn: console.warn, error: console.error }
    const orig = savedConsole
    const capture = (fn: (...a: unknown[]) => void) => (...args: unknown[]) => {
      const text = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ')
      if (/warning|wasm|jbig2|jpx|openjpeg|font|fetch/i.test(text))
        for (const sink of activeWarningSinks) if (sink.length < 5) sink.push(text)
      fn(...args)
    }
    console.log = capture(orig.log)
    console.warn = capture(orig.warn)
    console.error = capture(orig.error)
  }
  return run()
    .then((result) => ({ result, warnings }))
    .finally(() => {
      const i = activeWarningSinks.indexOf(warnings)
      if (i >= 0) activeWarningSinks.splice(i, 1)
      if (activeWarningSinks.length === 0 && savedConsole) {
        console.log = savedConsole.log
        console.warn = savedConsole.warn
        console.error = savedConsole.error
        savedConsole = null
      }
    })
}

/**
 * Virtualized page view: pre-sized placeholders per page, canvases rasterized
 * only near the viewport (see `mountPdfView`). Scrolls to `pageStart` on first
 * render; zoom re-fits without re-rasterizing everything.
 */
function PdfPages({
  url,
  pageStart,
  pageRange,
  zoom,
}: {
  url: string
  pageStart?: number
  /** When set, only these document pages are rendered. */
  pageRange?: { start: number; end: number }
  zoom: number
}) {
  const s = useStyles()
  const containerRef = useRef<HTMLDivElement>(null)
  // Read as PRIMITIVES, and rebuilt inside the effect below. The parent creates
  // `pageRange` fresh on every render, so depending on the object would remount
  // the whole view on every keystroke elsewhere in the modal.
  const rangeStart = pageRange?.start
  const rangeEnd = pageRange?.end
  // Jump to the invoice's page only on the initial render — a zoom change
  // re-renders and must not yank the reviewer's scroll position around.
  const didInitialScroll = useRef(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  const [diagnostics, setDiagnostics] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  // Parsed document, cached per URL so zoom changes re-render pages without
  // re-fetching or re-parsing the whole PDF.
  const [doc, setDoc] = useState<PdfDoc | null>(null)
  // The mounted virtualized view; reused across zoom changes (never recreated
  // for zoom). Latest zoom in a ref so mounting reads it without a dep.
  const viewRef = useRef<PdfView | null>(null)
  const zoomRef = useRef(zoom)
  // Warnings accumulate across the per-page renders that fire over time.
  const warningsRef = useRef<Set<string>>(new Set())

  // Load once per URL; destroy the handle when the URL changes or on unmount.
  useEffect(() => {
    let cancelled = false
    let localDoc: PdfDoc | null = null
    setDoc(null)
    setRenderError(null)
    ;(async () => {
      try {
        // Lazy-load the renderer — PDF.js lives in its own chunk (pdfRender.ts).
        const { loadPdfDocument } = await import('./pdfRender')
        const parsed = await loadPdfDocument(url)
        if (cancelled) { void parsed.loadingTask.destroy(); return }
        localDoc = parsed
        setDoc(parsed)
      } catch (e) {
        if (!cancelled) setRenderError((e as Error)?.message ?? 'Failed to render the PDF.')
      }
    })()
    return () => {
      cancelled = true
      if (localDoc) void localDoc.loadingTask.destroy()
    }
  }, [url, retryNonce])

  // Mount the virtualized view when the doc lands. Zoom is applied through the
  // view (below), never by recreating it — so re-rasterizing is scoped to the
  // visible pages.
  useEffect(() => {
    const container = containerRef.current
    if (!container || !doc) return
    let cancelled = false
    warningsRef.current.clear()
    setDiagnostics(null)
    ;(async () => {
      try {
        const { mountPdfView } = await import('./pdfRender')
        const view = await mountPdfView({
          container,
          doc,
          pageStart,
          pageRange: rangeStart === undefined ? undefined : { start: rangeStart, end: rangeEnd ?? rangeStart },
          zoom: zoomRef.current,
          scrollToStart: !didInitialScroll.current,
          wrapWarnings: collectPdfWarnings,
          onWarnings: (w) => {
            for (const line of w) warningsRef.current.add(line)
            const joined = [...warningsRef.current].join(' | ').slice(0, 500)
            setDiagnostics(joined || null)
          },
          onError: (m) => setRenderError(m),
          isCancelled: () => cancelled,
        })
        didInitialScroll.current = true
        if (cancelled) {
          view.destroy()
          return
        }
        viewRef.current = view
        setRenderError(null)
        // A zoom change during the async mount would have hit a null viewRef.
        view.setZoom(zoomRef.current)
      } catch (e) {
        if (!cancelled) setRenderError((e as Error)?.message ?? 'Failed to render the PDF.')
      }
    })()
    return () => {
      cancelled = true
      viewRef.current?.destroy()
      viewRef.current = null
    }
    // The range bounds are in the deps: switching between this invoice's pages
    // and the whole packet rebuilds the view. The parsed document is NOT
    // re-fetched — the effect above owns that, keyed on the URL — so the toggle
    // costs a re-layout, not another download.
  }, [doc, pageStart, rangeStart, rangeEnd])

  // Zoom re-fits placeholders and re-renders only the visible pages.
  useEffect(() => {
    zoomRef.current = zoom
    viewRef.current?.setZoom(zoom)
  }, [zoom])

  // Container stays mounted through an error (hidden) so the render effect's
  // ref survives — unmounting it stranded every later render at the null check.
  return (
    <>
      {renderError ? (
        <div className={s.placeholder}>
          <DocumentRegular fontSize={36} />
          <Body1>Couldn’t render the document.</Body1>
          <Caption1>{renderError}</Caption1>
          <Button size="small" onClick={() => setRetryNonce((n) => n + 1)}>Retry</Button>
        </div>
      ) : (
        diagnostics && <Caption1>Preview diagnostics: {diagnostics}</Caption1>
      )}
      <div ref={containerRef} className={s.pages} style={renderError ? { display: 'none' } : undefined} />
    </>
  )
}

interface Props {
  upload: Upload | undefined | 'loading'
  pageStart?: number
  pageEnd?: number
}

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

export function PdfPane({ upload, pageStart, pageEnd }: Props) {
  const s = useStyles()
  const uploadId = upload !== 'loading' ? upload?.id : undefined
  const hasFile = upload !== 'loading' && !!upload?.object_key
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' })
  const [zoom, setZoom] = useState(1)
  // One scan often holds several invoices. Default to THIS record's pages, so a
  // reviewer is shown their own bill rather than the stack it arrived in, and
  // offer the rest behind a toggle — seeing the neighbouring pages is how a
  // wrong boundary gets caught, so it must stay one click away.
  const [showWholeDocument, setShowWholeDocument] = useState(false)

  useEffect(() => {
    if (!uploadId || !hasFile) return
    const cached = cachedUrl(uploadId)
    if (cached) {
      setFetchState({ status: 'ready', url: cached })
      return
    }
    let cancelled = false
    setFetchState({ status: 'loading' })
    getUploadPdfUrl(uploadId)
      .then((signed) => {
        if (cancelled) return
        cacheUrl(uploadId, signed.url, signed.expires_at)
        setFetchState({ status: 'ready', url: signed.url })
      })
      .catch((e) => {
        if (!cancelled) setFetchState({ status: 'error', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [uploadId, hasFile])

  if (upload === 'loading') {
    return <div className={s.placeholder}><Spinner size="small" label="Loading document…" /></div>
  }
  if (!upload || !hasFile) {
    return (
      <div className={s.placeholder}>
        <DocumentRegular fontSize={32} />
        <Body1>No source document linked to this invoice.</Body1>
      </div>
    )
  }

  // The signed URL is the only way to the file — there is no durable link to
  // open, so the new-tab button waits for it.
  const signedUrl = fetchState.status === 'ready' ? fetchState.url : undefined
  const open = () => { if (signedUrl) window.open(signedUrl, '_blank', 'noopener') }
  // Page span first (more useful to the reviewer than the filename), then name.
  const pageLabel = pageStart
    ? pageEnd && pageEnd !== pageStart
      ? `Pages ${pageStart}–${pageEnd}`
      : `Page ${pageStart}`
    : ''

  // A span is restrictable only when the record actually claims one. A record
  // with no span (an older row, or a stub for a page nothing claimed) shows the
  // whole file, which is the only honest thing to show.
  const span = pageStart ? { start: pageStart, end: pageEnd ?? pageStart } : undefined
  const restricted = span !== undefined && !showWholeDocument

  let body
  if (fetchState.status === 'loading') {
    body = <div className={s.placeholder}><Spinner size="small" label="Loading document…" /></div>
  } else if (fetchState.status === 'ready') {
    body = (
      <PdfPages
        url={fetchState.url}
        pageStart={pageStart}
        pageRange={restricted ? span : undefined}
        zoom={zoom}
      />
    )
  } else {
    body = (
      <div className={s.placeholder}>
        <DocumentRegular fontSize={36} />
        <Body1>Couldn’t load the document for inline preview.</Body1>
        <Caption1>{fetchState.message}</Caption1>
      </div>
    )
  }

  return (
    <div className={s.root}>
      <div className={s.bar}>
        <Caption1>
          {pageLabel ? `${pageLabel} · ` : ''}
          {upload.file_name ?? 'Document'}
          {restricted ? ' · this invoice only' : span ? ' · whole document' : ''}
        </Caption1>
        <div className={s.zoomGroup}>
          {fetchState.status === 'ready' && span && (
            // Only worth offering when the record claims a span AND the file has
            // more than it: a one-invoice upload has no "rest" to show.
            <Button
              appearance="subtle"
              size="small"
              onClick={() => setShowWholeDocument((v) => !v)}
            >
              {restricted ? 'Show whole document' : 'Show this invoice only'}
            </Button>
          )}
          {fetchState.status === 'ready' && (
            <>
              <Button
                appearance="subtle"
                size="small"
                icon={<ZoomOutRegular />}
                aria-label="Zoom out"
                disabled={zoom <= ZOOM_MIN}
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))}
              />
              <Caption1 className={s.zoomLabel}>{Math.round(zoom * 100)}%</Caption1>
              <Button
                appearance="subtle"
                size="small"
                icon={<ZoomInRegular />}
                aria-label="Zoom in"
                disabled={zoom >= ZOOM_MAX}
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))}
              />
            </>
          )}
          <Button appearance="subtle" size="small" icon={<OpenRegular />} disabled={!signedUrl} onClick={open}>
            Open in new tab
          </Button>
        </div>
      </div>
      {body}
    </div>
  )
}
